import { Box, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import ExportButton from '~/components/Buttons/ExportButton';
import DoseItem from '~/components/DoseItem/DoseItem';
import ImageItem from '~/components/ImageItem/ImageItem';
import RegistrationItem from '~/components/RegistrationItem/RegistrationItem';
import SelectableListItem from '~/components/SelectableListItem/SelectableListItem';
import StructureSetItem from '~/components/Structures/StructureSetItem';
import { Modality } from '~/domain/dicom/modality';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import useExportableItems from '~/hooks/export/useExportableItems';
import usePrimaryImage from '~/hooks/image/usePrimaryImage';
import useStructureSetSelection from '~/hooks/structure-set/useStructureSetSelection';
import {
    useActivateRegistration,
    useRegistrationState,
} from '~/hooks/registration/useRegistrationStore';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import { currentStructureSetIdState } from '~/state/atoms/structureSet/structureSetAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { exportDatasetsModalOpenState } from '~/state/atoms/viewerAtoms';
import StepHeader from '../StepHeader';

export default function ExportContent() {
    const setExportModalOpen = useSetRecoilState(exportDatasetsModalOpenState);
    const intl = useIntl();
    const {
        images: exportableImages,
        structureSets: exportableStructureSets,
        doses: exportableDoses,
        registrations: exportableRegistrations,
        notExportedCount,
    } = useExportableItems();
    const { primaryImage, handleChangePrimaryImage } = usePrimaryImage();
    const { handleStructureSetChange } = useStructureSetSelection({});
    const currentDose = useRecoilValue(currentDoseState);
    const currentStructureSetId = useRecoilValue(currentStructureSetIdState);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const { setEnvironmentDose } = useViewportEnvironment();
    const activateRegistration = useActivateRegistration();
    const registrationState = useRegistrationState();

    const handleDoseSelect = (dose: DoseMatrix) => {
        setEnvironmentDose([dose], selectedPatientId ?? '');
    };

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Box flex={1} overflow="auto" minHeight={0}>
                <StepHeader
                    titleId="workflow.export"
                    descriptionId="workflow.export.description"
                />
                <Box px={1.5} py={1}>
                    <ExportButton
                        notExportedItemsCount={notExportedCount}
                        onOpenExportModal={() => setExportModalOpen(true)}
                    />
                </Box>

                {exportableImages.length > 0 && (
                    <Box px={1.5} pt={1.5} pb={1}>
                        <Typography
                            fontSize={12}
                            color="primary"
                            fontWeight={500}
                        >
                            <FormattedMessage id="images" />
                        </Typography>
                    </Box>
                )}
                {exportableImages.map((img) => (
                    <Box
                        key={img.dicomUid}
                        borderTop="1px solid"
                        borderColor="divider"
                    >
                        <ImageItem
                            image={img}
                            isSelected={
                                primaryImage?.seriesUid === img.seriesUid
                            }
                            onSelect={() =>
                                handleChangePrimaryImage({
                                    seriesInstanceUID: img.seriesUid,
                                    studyInstanceUID: img.studyUid,
                                    label:
                                        img.seriesMetadata.seriesDescription ||
                                        intl.formatMessage({
                                            id: 'unknown.series',
                                        }),
                                    modality: img.seriesMetadata
                                        .modality as Modality,
                                    isLoaded: true,
                                    imageObject: img,
                                })
                            }
                        />
                    </Box>
                ))}

                {exportableStructureSets.length > 0 && (
                    <Box px={1.5} pt={1.5} pb={1}>
                        <Typography
                            fontSize={12}
                            color="primary"
                            fontWeight={500}
                        >
                            <FormattedMessage id="structureSets" />
                        </Typography>
                    </Box>
                )}
                {exportableStructureSets.map((ss) => {
                    const available = {
                        sopInstanceUID: ss.dicomUid,
                        label:
                            ss.structureSetLabel ||
                            intl.formatMessage({
                                id: 'structureSet',
                            }),
                        isLoaded: true,
                        structureSetObject: ss,
                    };
                    return (
                        <Box
                            key={ss.dicomUid}
                            borderTop="1px solid"
                            borderColor="divider"
                        >
                            <StructureSetItem
                                structureSet={available}
                                isActive={currentStructureSetId === ss.dicomUid}
                                disabled={false}
                                onClick={() =>
                                    handleStructureSetChange(available)
                                }
                            />
                        </Box>
                    );
                })}

                {exportableDoses.length > 0 && (
                    <Box px={1.5} pt={1.5} pb={1}>
                        <Typography
                            fontSize={12}
                            color="primary"
                            fontWeight={500}
                        >
                            <FormattedMessage id="doses" />
                        </Typography>
                    </Box>
                )}
                {exportableDoses.map((dose) => (
                    <Box
                        key={dose.dicomUid}
                        borderTop="1px solid"
                        borderColor="divider"
                    >
                        <SelectableListItem
                            isSelected={currentDose?.dicomUid === dose.dicomUid}
                            onClick={() => handleDoseSelect(dose)}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 1,
                            }}
                        >
                            <DoseItem
                                dose={dose}
                                label={dose.plan?.label ?? 'Dose'}
                            />
                        </SelectableListItem>
                    </Box>
                ))}

                {exportableRegistrations.length > 0 && (
                    <Box px={1.5} pt={1.5} pb={1}>
                        <Typography
                            fontSize={12}
                            color="primary"
                            fontWeight={500}
                        >
                            <FormattedMessage id="registrations" />
                        </Typography>
                    </Box>
                )}
                {exportableRegistrations.map((reg) => {
                    const secondaryImage = exportableImages.find(
                        (img) =>
                            img.frameOfReferenceUid ===
                            reg.targetFrameOfReferenceUid
                    );
                    const sourceImage = exportableImages.find(
                        (img) =>
                            img.frameOfReferenceUid ===
                            reg.sourceFrameOfReferenceUid
                    );
                    const canActivate = !!secondaryImage && !!sourceImage;
                    return (
                        <Box
                            key={reg.dicomUid}
                            borderTop="1px solid"
                            borderColor="divider"
                        >
                            <RegistrationItem
                                label={reg.contentLabel || reg.dicomUid}
                                approvalStatus={reg.approvalStatus}
                                isSelected={
                                    registrationState.selectedSopInstanceUid ===
                                    reg.dicomUid
                                }
                                onClick={
                                    canActivate
                                        ? async () => {
                                              await handleChangePrimaryImage({
                                                  seriesInstanceUID:
                                                      sourceImage.seriesUid,
                                                  studyInstanceUID:
                                                      sourceImage.studyUid,
                                                  label:
                                                      sourceImage.seriesMetadata
                                                          .seriesDescription ||
                                                      intl.formatMessage({
                                                          id: 'unknown.series',
                                                      }),
                                                  modality: sourceImage
                                                      .seriesMetadata
                                                      .modality as Modality,
                                                  isLoaded: true,
                                                  imageObject: sourceImage,
                                              });
                                              activateRegistration(
                                                  reg.dicomUid,
                                                  secondaryImage.seriesUid
                                              );
                                          }
                                        : undefined
                                }
                                registration={reg}
                            />
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
